import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import useHankoAuth from '../../hooks/useHankoAuth';
import { loadTurnstile, postContact, TURNSTILE_SITEKEY } from '../../utils/turnstile';

// Oma yhteydenottolomake HubSpot-upotuksen tilalle (HubSpot-alasajon vaihe 2, 14.9.2026).
// Postaa tendera.fi:n rajapintaan, joka lähettää viestin Resendillä myynnille ja tallentaa KV:hen.
// Botsuoja: sama Cloudflare Turnstile -widget kuin sivustolla (hostname app.tendera.bid sallittu widgetissä).

const ContactUsModal = ({ open, onClose }) => {
    const { t, i18n } = useTranslation();
    const { user } = useHankoAuth();
    const [form] = Form.useForm();
    const [state, setState] = useState('idle'); // idle | sending | sent | error
    const [token, setToken] = useState('');
    const widgetRef = useRef(null);
    const widgetId = useRef(null);

    useEffect(() => {
        if (!open) { setState('idle'); setToken(''); widgetId.current = null; return undefined; }
        let cancelled = false;
        loadTurnstile().then((ts) => {
            if (cancelled || !widgetRef.current || widgetId.current !== null) return;
            widgetId.current = ts.render(widgetRef.current, {
                sitekey: TURNSTILE_SITEKEY,
                language: (i18n.language || 'fi').split('-')[0],
                appearance: 'interaction-only',
                callback: (tok) => setToken(tok),
                'expired-callback': () => setToken(''),
                'error-callback': () => setToken(''),
            });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [open, i18n.language]);

    const submit = async (values) => {
        setState('sending');
        try {
            await postContact({
                name: values.name, email: values.email, company: values.company || '', message: values.message,
                topic: 'app_yhteydenotto', page: `app:${window.location.pathname}`, utm: `lang=${i18n.language}`,
                'cf-turnstile-response': token,
            });
            setState('sent');
            form.resetFields(['message']);
        } catch (e) {
            setState('error');
            try { window.turnstile?.reset(widgetId.current); } catch (x) { /* noop */ }
            setToken('');
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="min(560px, 92vw)"
            centered
            destroyOnHidden
            title={t('sidebar.contactUs', 'Ota yhteyttä')}
            styles={{ content: { borderRadius: 16 }, header: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
        >
            {state === 'sent' ? (
                <Alert type="success" showIcon message={t('contactForm.sentTitle', 'Kiitos viestistäsi.')} description={t('contactForm.sentBody', 'Vastaamme yleensä yhden arkipäivän sisällä.')} />
            ) : (
                <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}
                    initialValues={{ email: user?.email || '', name: user?.name || user?.username || '' }}>
                    <p style={{ marginTop: 0, color: '#5f6d78' }}>{t('contactForm.lead', 'Kerro, missä voimme auttaa. Viesti menee suoraan Tenderan tiimille.')}</p>
                    <Form.Item name="name" label={t('contactForm.name', 'Nimi')} rules={[{ required: true, message: t('contactForm.required', 'Pakollinen tieto') }]}>
                        <Input maxLength={120} autoComplete="name" />
                    </Form.Item>
                    <Form.Item name="email" label={t('contactForm.email', 'Sähköposti')} rules={[{ required: true, type: 'email', message: t('contactForm.emailInvalid', 'Tarkista sähköpostiosoite') }]}>
                        <Input maxLength={200} autoComplete="email" />
                    </Form.Item>
                    <Form.Item name="company" label={t('contactForm.company', 'Yritys')}>
                        <Input maxLength={160} autoComplete="organization" />
                    </Form.Item>
                    <Form.Item name="message" label={t('contactForm.message', 'Viesti')} rules={[{ required: true, message: t('contactForm.required', 'Pakollinen tieto') }]}>
                        <Input.TextArea rows={5} maxLength={4000} showCount />
                    </Form.Item>
                    <div ref={widgetRef} style={{ marginBottom: 12 }} />
                    {state === 'error' && (
                        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('contactForm.errorTitle', 'Lähetys ei onnistunut.')} description={t('contactForm.errorBody', 'Yritä hetken kuluttua uudelleen tai kirjoita osoitteeseen support@tendera.bid.')} />
                    )}
                    <Button type="primary" htmlType="submit" loading={state === 'sending'} disabled={!token} block>
                        {t('contactForm.send', 'Lähetä viesti')}
                    </Button>
                </Form>
            )}
        </Modal>
    );
};

export default ContactUsModal;
